import React from 'react';
import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';
import { Button, Text } from '@musa.tech/design-system';

interface ApiErrorDisplayProps {
  error: Error;
  onRetry?: () => void;
  context?: 'dashboard' | 'coletas' | 'generic';
}

/**
 * Detecta o tipo de erro e retorna mensagem amigável + sugestão de ação.
 */
function parseErrorMessage(error: Error): { title: string; message: string; suggestion: string; icon: 'alert' | 'network' | 'server' } {
  const errorMsg = error.message.toLowerCase();

  // Erro de timeout
  if (errorMsg.includes('timeout') || errorMsg.includes('timed out')) {
    return {
      title: 'Tempo de resposta excedido',
      message: 'A requisição está demorando mais do que o esperado.',
      suggestion: 'Tente reduzir o período de datas ou os filtros selecionados, ou aguarde alguns instantes e tente novamente.',
      icon: 'alert'
    };
  }

  // Erro de rede
  if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('conexão')) {
    return {
      title: 'Erro de conexão',
      message: 'Não foi possível conectar ao servidor.',
      suggestion: 'Verifique sua conexão com a internet e tente novamente.',
      icon: 'network'
    };
  }

  // Erro 400 (Bad Request)
  if (errorMsg.includes('400')) {
    return {
      title: 'Requisição inválida',
      message: 'Os dados enviados não são válidos ou o sistema está sendo atualizado.',
      suggestion: 'Tente ajustar os filtros ou aguarde alguns minutos. Se o problema persistir, entre em contato com o suporte.',
      icon: 'server'
    };
  }

  // Erro 401/403 (Não autorizado)
  if (errorMsg.includes('401') || errorMsg.includes('403') || errorMsg.includes('não autorizado') || errorMsg.includes('unauthorized')) {
    return {
      title: 'Sessão expirada',
      message: 'Sua sessão expirou ou você não tem permissão para acessar estes dados.',
      suggestion: 'Faça login novamente para continuar.',
      icon: 'alert'
    };
  }

  // Erro 500 (Server Error)
  if (errorMsg.includes('500') || errorMsg.includes('internal server')) {
    return {
      title: 'Erro no servidor',
      message: 'Ocorreu um erro inesperado em nosso servidor.',
      suggestion: 'Estamos trabalhando para resolver o problema. Por favor, tente novamente em alguns minutos.',
      icon: 'server'
    };
  }

  // Erro genérico
  return {
    title: 'Erro ao carregar dados',
    message: 'Ocorreu um erro inesperado ao carregar os dados.',
    suggestion: 'Por favor, tente novamente. Se o problema persistir, entre em contato com o suporte.',
    icon: 'alert'
  };
}

export const ApiErrorDisplay = ({ error, onRetry, context = 'generic' }: ApiErrorDisplayProps) => {
  const { title, message, suggestion, icon } = parseErrorMessage(error);

  const IconComponent = icon === 'network' ? WifiOff : icon === 'server' ? AlertCircle : AlertCircle;

  return (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <div className="max-w-md w-full bg-card rounded-lg shadow-md p-6 border border-destructive/20">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <IconComponent className="h-8 w-8 text-destructive" />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <Text variant="heading" size="small" className="text-neutral-900">
                {title}
              </Text>
              <Text variant="body" size="small" className="mt-2 block text-neutral-600">
                {message}
              </Text>
              <Text variant="body" size="small" className="mt-2 block italic text-neutral-500">
                {suggestion}
              </Text>
            </div>

            {onRetry && (
              <div className="pt-2">
                <Button
                  onClick={onRetry}
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Tentar novamente
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
